/**
 * **Which company a screen shows, said on the screen** (F356; Thomas, 2026-10-11). The switcher lists profiles only,
 * so the company in view — the scope in the address, put there by opening a company — is named where its records
 * are shown, in a sentence, never in the banner and never as a list to choose from:
 *
 * - on a screen that **follows** the company in view (Documents, Customers and suppliers, Bank, Beliefs), whenever
 *   the profile holds more than one company: *Showing X · its number. Open another company from Structure.*;
 * - on a screen that **narrows** to it only when one was put in view (the audit log and Clarify), when one was:
 *   the same sentence, and the whole profile back in one click.
 *
 * A profile holding one company says nothing: there is no other it could be.
 */
import { Link, useRouterState } from '@tanstack/react-router';
import { useEntities } from '../lib/api.ts';
import { useT } from '../lib/preferences.tsx';
import { inView, inViewWords, say, scopeUse } from '../lib/scope.ts';
import { useTenant } from '../lib/tenant.tsx';
import { useChosenScope, useScope } from '../router.tsx';

export function InViewNote() {
  const { kind } = useTenant();
  const use = scopeUse(useRouterState({ select: (s) => s.location.pathname }));
  // A person's own profile holds no company, and most screens do not use one: neither asks for the list.
  if (kind !== 'org' || use === null) return null;
  return <CompanyInView use={use} />;
}

function CompanyInView({ use }: { use: 'follows' | 'narrows' }) {
  const t = useT();
  const { tenantId, membership } = useTenant();
  const entities = useEntities(tenantId);
  const [inForce] = useScope();
  const chosen = useChosenScope();
  const list = entities.data;
  if (list === undefined) return null;
  if (use === 'follows' && list.length < 2) return null;
  if (use === 'narrows' && chosen === null) return null;
  const words = inViewWords(inView(use === 'narrows' ? chosen : inForce, list, membership.orgnr));
  if (words === null) return null;

  // Underlined, not only coloured: a link inside a sentence must be told from the words around it without colour.
  const link =
    'text-accent-text underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-accent';
  return (
    <p
      data-testid="in-view-note"
      className="border-b border-border bg-surface px-6 py-2 text-sm text-muted"
    >
      <span data-testid="in-view-company" className="text-text">
        {say(t, words)}
      </span>{' '}
      {use === 'follows' ? (
        <Link to="/structure" search={(prev) => prev} className={link}>
          {t('scope.inView.another')}
        </Link>
      ) : (
        // The whole profile is the address with no company in it: the default company does not narrow these.
        <Link to="." search={(prev) => ({ ...prev, scope: undefined })} className={link}>
          {t('scope.inView.whole')}
        </Link>
      )}
    </p>
  );
}
